import { useState, useEffect, useCallback, memo } from 'react'
import { Link, useNavigate, useRouterState } from '@tanstack/react-router'
import { Mail, Lock, User, Eye, EyeOff, AlertCircle, CheckCircle, Building2, Users2, Plus, X } from 'lucide-react'
import { useAuthService } from '../../services/authService'
import { useAuthStore } from '../../store/authStore'
import { AuthClient } from '../../clients'
import { IRegisterFormData, ISetPasswordFormData, UserType } from '../../types/UserTypes'
import EventlyAccountNotice from './EventlyAccountNotice'
import UserTypeTermsDialog from '../sections/terms-of-service/user-type-terms-dialog'
import PrivacyPolicyDialog from '../sections/privacy-policy/privacy-policy-dialog'


const ORGANIZATION_ID = import.meta.env.VITE_ORGANIZATION_ID;

// Password strength indicator component - moved outside to prevent re-creation
interface PasswordStrength {
  length: boolean;
  uppercase: boolean;
  lowercase: boolean;
  number: boolean;
  special: boolean;
}

const PasswordStrengthIndicator = ({ passwordStrength }: { passwordStrength: PasswordStrength }) => (
  <div className="mt-2 space-y-2">
    <p className="text-sm text-gray-600">Password requirements:</p>
    <div className="space-y-1">
      {[
        { key: 'length', label: 'At least 8 characters', value: passwordStrength.length },
        { key: 'uppercase', label: 'One uppercase letter', value: passwordStrength.uppercase },
        { key: 'lowercase', label: 'One lowercase letter', value: passwordStrength.lowercase },
        { key: 'number', label: 'One number', value: passwordStrength.number },
        { key: 'special', label: 'One special character', value: passwordStrength.special }
      ].map(({ key, label, value }) => (
        <div key={key} className="flex items-center text-sm">
          {value ? (
            <CheckCircle className="text-green-500 mr-2" size={16} />
          ) : (
            <div className="w-4 h-4 border-2 border-gray-300 rounded-full mr-2"></div>
          )}
          <span className={value ? 'text-green-600' : 'text-gray-500'}>{label}</span>
        </div>
      ))}
    </div>
  </div>
);

// Success component for initial signup - moved outside to prevent re-creation
const SignUpSuccess = () => (
  <div className="flex min-h-[70vh] flex-col items-center justify-center p-4">
    <span className="mb-5 rounded-full border-2 border-orange-500 bg-orange-500 p-2">
      <CheckCircle className="h-6 w-6 text-white" />
    </span>
    <div className="bg-white text-center">
      <h1 className="mb-4 text-3xl font-bold text-orange-500">Sign Up Successful!</h1>
      <p className="mb-6 text-lg text-gray-700">Please check your email to complete the sign up process.</p>
      <Link to="/">
        <button className="w-full px-10 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors md:w-auto">
          Go to Home Page
        </button>
      </Link>
    </div>
  </div>
);

// Success component for password setting - moved outside to prevent re-creation
const PasswordSetSuccess = () => (
  <div className="flex min-h-[70vh] flex-col items-center justify-center p-4">
    <span className="mb-5 rounded-full border-2 border-green-500 bg-green-500 p-2">
      <CheckCircle className="h-6 w-6 text-white" />
    </span>
    <div className="bg-white text-center">
      <h1 className="mb-4 text-3xl font-bold text-green-500">Password Set Successfully!</h1>
      <p className="mb-6 text-lg text-gray-700">You can now sign in to your account.</p>
      <Link to="/login">
        <button className="w-full px-10 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors md:w-auto">
          Sign In
        </button>
      </Link>
    </div>
  </div>
);

// SetPassword component props interface
interface SetPasswordProps {
  resetId: string;
  formData: {
    password: string;
    confirmPassword: string;
  };
  showPassword: boolean;
  showConfirmPassword: boolean;
  errors: { [key: string]: string };
  errMsg: string;
  isLoading: boolean;
  passwordStrength: PasswordStrength;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  handlePasswordSubmit: (e: React.FormEvent) => void;
  setShowPassword: (show: boolean) => void;
  setShowConfirmPassword: (show: boolean) => void;
}

// Password setting component - memoized to prevent re-creation
const SetPassword = memo(({ 
  resetId, 
  formData, 
  showPassword, 
  showConfirmPassword, 
  errors, 
  errMsg, 
  isLoading, 
  passwordStrength, 
  handleChange, 
  handlePasswordSubmit, 
  setShowPassword, 
  setShowConfirmPassword 
}: SetPasswordProps) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
    <div className="max-w-md w-full space-y-8">
      <div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Set Your Password
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Please create a secure password for your account
        </p>
      </div>
      <form className="mt-8 space-y-6" onSubmit={handlePasswordSubmit}>
        <div className="space-y-4">
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
              Password
            </label>
            <div className="mt-1 relative">
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                required
                value={formData.password}
                onChange={handleChange}
                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-orange-500 focus:border-orange-500 focus:z-10 sm:text-sm"
                placeholder="Enter your password"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5 text-gray-400" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
            {errors.password && (
              <p className="mt-2 text-sm text-red-600 flex items-center">
                <AlertCircle className="h-4 w-4 mr-1" />
                {errors.password}
              </p>
            )}
            <PasswordStrengthIndicator passwordStrength={passwordStrength} />
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
              Confirm Password
            </label>
            <div className="mt-1 relative">
              <input
                id="confirmPassword"
                name="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                required
                value={formData.confirmPassword}
                onChange={handleChange}
                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-orange-500 focus:border-orange-500 focus:z-10 sm:text-sm"
                placeholder="Confirm your password"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-5 w-5 text-gray-400" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="mt-2 text-sm text-red-600 flex items-center">
                <AlertCircle className="h-4 w-4 mr-1" />
                {errors.confirmPassword}
              </p>
            )}
          </div>
        </div>

        {errMsg && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <p className="text-sm text-red-800">{errMsg}</p>
              </div>
            </div>
          </div>
        )}

        <div>
          <button
            type="submit"
            disabled={isLoading}
            className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Setting Password...
              </div>
            ) : (
              'Set Password'
            )}
          </button>
        </div>
      </form>
    </div>
  </div>
));

const Signup = () => {
  const navigate = useNavigate()
  const { location } = useRouterState()
  const { handleSignIn } = useAuthService()
  const { errMsg, setErrMsg } = useAuthStore()
  
  // URL query parameters
  const searchParams = new URLSearchParams(location.search)
  const resetId = searchParams.get("reset_id")
  const [isResetIdValid, setIsResetIdValid] = useState(false)
  
  // Form states
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '+63',
    password: '',
    confirmPassword: '',
    userType: 'supplier' as 'supplier' | 'celebrant',
    celebrants: [''] // Array to store up to 2 celebrant names
  })
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [isPasswordSetSuccess, setIsPasswordSetSuccess] = useState(false)
  const [selectedType, setSelectedType] = useState<UserType | null>(null)
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  })
  
  // Terms and Privacy Policy states
  const [isTermsOfServiceOpen, setIsTermsOfServiceOpen] = useState(false)
  const [isPrivacyPolicyOpen, setIsPrivacyPolicyOpen] = useState(false)
  const [pendingRegisterData, setPendingRegisterData] = useState<{ email: string; phone: string; user_type: UserType } | null>(null)
  const [hasReadTerms, setHasReadTerms] = useState(false)
  const [hasReadPrivacy, setHasReadPrivacy] = useState(false)

  // Validate reset ID from URL
  const handleValidateResetID = () => {
    if (!resetId) return
    
    setIsLoading(true)
    AuthClient.validateResetID(resetId)
      .then(() => {
        setIsResetIdValid(true)
      })
      .catch(() => {
        setIsResetIdValid(false)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  // Handle initial signup (email + phone only)
  const handleUserSignUp = async (data: { email: string; phone: string; user_type: UserType }) => {
    setIsLoading(true)
    setErrMsg("")
    
    try {
      // Remove +63 prefix and get only the last 10 digits
      const phoneDigits = data.phone.replace('+63', '0')
      
      const signupData: IRegisterFormData = {
        email: data.email,
        phone: phoneDigits,
        user_type: data.user_type,
        organization: ORGANIZATION_ID
      }
      
      await AuthClient.signUp(signupData)
      setIsSuccess(true)
    } catch (error: any) {
      let err = "Email already taken"
      if (error.response?.status !== 409) {
        err = "Failed to sign up. Please try again."
      }
      setErrMsg(err)
    } finally {
      setIsLoading(false)
    }
  }

  // Terms and Privacy Policy handlers
  const handleTermsDialogClose = (open: boolean) => {
    setIsTermsOfServiceOpen(open)
    // Don't mark as read when dialog is closed - only when scrolled to bottom
  }

  const handlePrivacyDialogClose = (open: boolean) => {
    setIsPrivacyPolicyOpen(open)
    // Don't mark as read when dialog is closed - only when scrolled to bottom
  }

  const handleTermsScrollToBottom = () => {
    setHasReadTerms(true)
  }

  const handlePrivacyScrollToBottom = () => {
    setHasReadPrivacy(true)
  }

  // Continue handlers for sequential agreement flow
  const handleAgreeTermsContinue = () => {
    setIsTermsOfServiceOpen(false)
    setIsPrivacyPolicyOpen(true)
  }

  const handleAgreePrivacyContinue = () => {
    if (!pendingRegisterData) return
    setIsPrivacyPolicyOpen(false)
    setIsLoading(true)
    
    // Now proceed with the actual signup
    handleUserSignUp(pendingRegisterData)
  }

  // Handle password setting (when reset_id is valid)
  const handleSetPassword = async (data: ISetPasswordFormData) => {
    setIsLoading(true)
    setErrMsg("")
    
    try {
      await AuthClient.setPassword({
        reset_id: data.reset_id,
        password: data.password
      })
      setIsPasswordSetSuccess(true)
    } catch (error: any) {
      setErrMsg("Failed to set password. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  // useEffect hooks
  useEffect(() => {
    if (resetId) {
      handleValidateResetID()
    }
  }, [resetId])

  useEffect(() => {
    return () => {
      setErrMsg("")
    }
  }, [])

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }

    // Handle password strength check
    if (name === 'password') {
      setPasswordStrength({
        length: value.length >= 8,
        uppercase: /[A-Z]/.test(value),
        lowercase: /[a-z]/.test(value),
        number: /\d/.test(value),
        special: /[!@#$%^&*(),.?":{}|<>]/.test(value)
      })
    }

    // Update form data
    setFormData(prev => {
      const newData = { ...prev, [name]: value }
      
      // Reset celebrants when switching to supplier, or reset name when switching to celebrant
      if (name === 'userType') {
        newData.name = ''
        newData.celebrants = ['']
      }
      
      return newData
    })
  }, [errors])

  const handlePhoneChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    
    // Clear error when user starts typing
    if (errors.phone) {
      setErrors(prev => ({
        ...prev,
        phone: ''
      }))
    }
    
    // If user is trying to delete the +63 prefix, allow it but keep it minimal
    if (value === '' || value === '+' || value === '+6' || value === '+63') {
      setFormData(prev => ({
        ...prev,
        phone: '+63'
      }))
      return
    }
    
    // Check if the value starts with +63
    if (!value.startsWith('+63')) {
      // If it doesn't start with +63, extract only digits and add +63 prefix
      const digitsOnly = value.replace(/\D/g, '')
      const limitedDigits = digitsOnly.slice(0, 10)
      const phoneValue = limitedDigits ? `+63${limitedDigits}` : '+63'
      
      setFormData(prev => ({
        ...prev,
        phone: phoneValue
      }))
    } else {
      // If it starts with +63, extract digits after +63 and limit to 10
      const afterPrefix = value.substring(3) // Remove +63
      const digitsOnly = afterPrefix.replace(/\D/g, '')
      const limitedDigits = digitsOnly.slice(0, 10)
      const phoneValue = `+63${limitedDigits}`
      
      setFormData(prev => ({
        ...prev,
        phone: phoneValue
      }))
    }
  }, [errors.phone])

  const handleCelebrantChange = useCallback((index: number, value: string) => {
    // Clear error when user starts typing
    if (errors[`celebrant_${index}`]) {
      setErrors(prev => ({
        ...prev,
        [`celebrant_${index}`]: ''
      }))
    }

    setFormData(prev => ({
      ...prev,
      celebrants: prev.celebrants.map((celebrant, i) => i === index ? value : celebrant)
    }))
  }, [errors])

  const addCelebrant = useCallback(() => {
    if (formData.celebrants.length < 2) {
      setFormData(prev => ({
        ...prev,
        celebrants: [...prev.celebrants, '']
      }))
    }
  }, [formData.celebrants.length])

  const removeCelebrant = useCallback((index: number) => {
    if (formData.celebrants.length > 1) {
      setFormData(prev => ({
        ...prev,
        celebrants: prev.celebrants.filter((_, i) => i !== index)
      }))
    }
  }, [formData.celebrants.length])

  // Validation for initial signup (email + phone only)
  const validateInitialSignup = () => {
    const newErrors: { [key: string]: string } = {}

    if (!formData.email) {
      newErrors.email = 'Email is required'
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }

    if (!formData.phone) {
      newErrors.phone = 'Phone number is required'
    } else if (!/^\+63\d{10}$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid 10-digit phone number'
    }

    if (!selectedType) {
      newErrors.userType = 'Please select your user type'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Validation for password setting (when reset_id is valid)
  const validatePasswordForm = () => {
    const newErrors: { [key: string]: string } = {}

    if (!formData.password) {
      newErrors.password = 'Password is required'
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters'
    } else if (!Object.values(passwordStrength).every(Boolean)) {
      newErrors.password = 'Password does not meet requirements'
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password'
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle initial signup form submission
  const handleInitialSignupSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateInitialSignup()) {
      return
    }

    if (!selectedType) {
      return
    }

    // Stage the data and open Terms dialog
    const signupData = {
      email: formData.email,
      phone: formData.phone,
      user_type: selectedType
    }
    setPendingRegisterData(signupData)
    setIsTermsOfServiceOpen(true)
  }

  // Handle password setting form submission
  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validatePasswordForm()) {
      return
    }

    if (!resetId) {
      setErrMsg("Invalid reset ID")
      return
    }

    await handleSetPassword({
      reset_id: resetId,
      password: formData.password,
      re_password: formData.confirmPassword
    })
  }




  // Conditional rendering based on state
  if (isPasswordSetSuccess) {
    return <PasswordSetSuccess />
  }

  if (isSuccess) {
    return <SignUpSuccess />
  }

  if (isResetIdValid && resetId) {
    return (
      <SetPassword 
        resetId={resetId}
        formData={formData}
        showPassword={showPassword}
        showConfirmPassword={showConfirmPassword}
        errors={errors}
        errMsg={errMsg}
        isLoading={isLoading}
        passwordStrength={passwordStrength}
        handleChange={handleChange}
        handlePasswordSubmit={handlePasswordSubmit}
        setShowPassword={setShowPassword}
        setShowConfirmPassword={setShowConfirmPassword}
      />
    )
  }

  // Initial signup form (email + phone only)
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Join PhilTrade</h2>
          <p className="text-gray-600">
            Connect with the premier wedding supplier exhibit platform
          </p>
        </div>

        {/* Signup Form */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          {/* Evently Account Notice */}
          <EventlyAccountNotice variant="signup" />

          {(errors.general || errMsg) && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <AlertCircle className="text-red-600 mr-2" size={20} />
                <p className="text-red-800 text-sm">{errors.general || errMsg}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleInitialSignupSubmit} className="space-y-6">
            {/* User Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                I am a:
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setSelectedType(3)}
                  className={`relative cursor-pointer rounded-lg border-2 p-4 transition-colors duration-200 ${
                    selectedType === 3 
                      ? 'border-orange-500 bg-orange-50' 
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="flex md:flex-row flex-col items-center space-x-3">
                    <Building2 className={`w-6 h-6 ${selectedType === 3 ? 'text-orange-600' : 'text-gray-400'}`} />
                    <div>
                      <div className="font-medium text-gray-900">Wedding Supplier</div>
                      <div className="text-sm text-gray-500">Showcase at exhibits</div>
                    </div>
                  </div>
                </button>
                
                <button
                  type="button"
                  onClick={() => setSelectedType(2)}
                  className={`relative cursor-pointer rounded-lg border-2 p-4 transition-colors duration-200 ${
                    selectedType === 2 
                      ? 'border-orange-500 bg-orange-50' 
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="flex md:flex-row flex-col items-center space-x-3">
                    <Users2 className={`w-6 h-6 ${selectedType === 2 ? 'text-orange-600' : 'text-gray-400'}`} />
                    <div>
                      <div className="font-medium text-gray-900">
                      Wedding Celebrant or Debutant
                      </div>
                      <div className="text-sm text-gray-500">Find suppliers</div>
                    </div>
                  </div>
                </button>
              </div>
              {errors.userType && (
                <p className="mt-1 text-sm text-red-600">{errors.userType}</p>
              )}
            </div>


            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="text-gray-400" size={20} />
                </div>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200 ${
                    errors.email ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter your email"
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            {/* Phone Field */}
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="text-gray-400" size={20} />
                </div>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-colors duration-200 ${
                    errors.phone ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="+63XXXXXXXXXX"
                />
              </div>
              {errors.phone && (
                <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
              )}
              {!errors.phone && (
                <p className="mt-1 md:text-start text-center text-sm text-gray-500">
                  Enter your 10-digit phone number (e.g., 9123456789)
                </p>
              )}
            </div>


            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-orange-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-orange-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Creating account...' : `Join as ${selectedType === 3 ? 'Supplier' : 'Celebrant'}`}
            </button>
          </form>

          {/* Login Link */}
          <div className="mt-6 text-center">
            <p className="text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="text-orange-600 hover:text-orange-700 font-medium">
                Sign in
              </Link>
            </p>
          </div>
        </div>

        {/* Benefits Section */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {formData.userType === 'supplier' ? 'Benefits for Suppliers' : 'Benefits for Celebrants'}
          </h3>
          <div className="space-y-3">
            {formData.userType === 'supplier' ? (
              <>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-primary-600 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-sm text-gray-600">Showcase your services at professional exhibits</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-primary-600 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-sm text-gray-600">Connect directly with engaged couples</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-primary-600 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-sm text-gray-600">Grow your business with quality leads</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-primary-600 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-sm text-gray-600">Access marketing and promotional support</p>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-primary-600 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-sm text-gray-600">Discover top wedding suppliers in one place</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-primary-600 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-sm text-gray-600">Compare services and prices easily</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-primary-600 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-sm text-gray-600">Book services directly at exhibits</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-primary-600 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-sm text-gray-600">Get exclusive deals and packages</p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Programmatic dialogs for sequential agreement flow */}
      <UserTypeTermsDialog
        open={isTermsOfServiceOpen}
        setOpen={handleTermsDialogClose}
        userType={selectedType}
        showAgreementControls
        onScrollToBottom={handleTermsScrollToBottom}
        onContinue={handleAgreeTermsContinue}
      />
      <PrivacyPolicyDialog
        open={isPrivacyPolicyOpen}
        setOpen={handlePrivacyDialogClose}
        showAgreementControls
        onScrollToBottom={handlePrivacyScrollToBottom}
        onContinue={handleAgreePrivacyContinue}
      />
    </div>
  )
}

export default Signup
